import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Breadcrumbs
} from '@mui/material';
import { signUp } from '@aws-amplify/auth';
import { createUser } from '../api/user';
import { createStripeSubscription } from '../api/subscription'; // NEW: API to create a Stripe subscription/charge
import { StripeWrapper } from '../components/stripe/StripeWrapper';
import PaymentForm from '../components/stripe/PaymentForm';

const steps = ['Account Details', 'Billing Information', 'Confirmation'];

export default function PremiumSignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    planId: '',
    planPrice: 0,
    billingInterval: 'monthly',
    stripePaymentMethodId: null,
    stripeCustomerId: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      number: /[0-9]/.test(password),
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      symbol: /[^A-Za-z0-9]/.test(password)
    };
    return Object.values(checks).every(Boolean);
  };

  const validateCurrentStep = () => {
    switch (activeStep) {
      case 0:
        if (!formData.email || !formData.password || !formData.confirmPassword) {
          setError('Please fill in all fields');
          return false;
        }
        if (!validatePassword(formData.password)) {
          setError('Password does not meet requirements');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        return true;

      case 1:
        if (!formData.firstName || !formData.lastName) {
          setError('Please fill in all fields');
          return false;
        }
        if (!formData.stripePaymentMethodId || !formData.planId) {
          setError('Please complete payment information');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 1. Create Stripe subscription (charge the card)
      const subscriptionResponse = await createStripeSubscription({
        paymentMethodId: formData.stripePaymentMethodId,
        planId: formData.stripePriceId, 
        planPrice: formData.planPrice,
        billingInterval: formData.billingInterval,
        email: formData.email,
      });
      if (!subscriptionResponse || subscriptionResponse.error) {
        throw new Error(subscriptionResponse?.error || 'Failed to create subscription');
      }
      // Update formData with Stripe customer ID
      const stripeCustomerId = subscriptionResponse.stripeCustomerId;
      setFormData(prev => ({ ...prev, stripeCustomerId }));

      // 2. Create Cognito user
      const cognitoUser = await signUp({
        username: formData.email,
        password: formData.password,
        attributes: {
          email: formData.email,
          given_name: formData.firstName,
          family_name: formData.lastName,
        },
      });

      // 3. Create user in backend
      const userResponse = await createUser({
        id: cognitoUser.userId,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        stripePaymentMethodId: formData.stripePaymentMethodId,
        stripeCustomerId: stripeCustomerId,
        planId: formData.planId,
        planPrice: formData.planPrice,
        billingInterval: formData.billingInterval
      });
      
      if (!userResponse || userResponse.error) {
        throw new Error('Failed to create user in backend');
      }

      // 4. Navigate to confirmation
      navigate('/signup/confirm', { state: { email: formData.email } });
    } catch (err) {
      setError(err.message || 'Failed to sign up');
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    if (activeStep === steps.length - 1) {
      await handleSubmit();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Breadcrumbs separator="|" aria-label="password requirements">
                <Typography variant="caption" color={formData.password.length >= 8 ? "success.main" : "text.secondary"}>
                  Min 8 characters
                </Typography>
                <Typography variant="caption" color={/[0-9]/.test(formData.password) ? "success.main" : "text.secondary"}>
                  1 Number
                </Typography>
                <Typography variant="caption" color={/[A-Z]/.test(formData.password) ? "success.main" : "text.secondary"}>
                  1 Uppercase
                </Typography>
                <Typography variant="caption" color={/[a-z]/.test(formData.password) ? "success.main" : "text.secondary"}>
                  1 Lowercase
                </Typography>
                <Typography variant="caption" color={/[^A-Za-z0-9]/.test(formData.password) ? "success.main" : "text.secondary"}>
                  1 Symbol
                </Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <StripeWrapper>
                <PaymentForm
                  formData={formData}
                  setFormData={setFormData}
                  onPaymentSuccess={() => setError('')}
                  onPaymentError={setError}
                />
              </StripeWrapper>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Review Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              Email: {formData.email}
            </Typography>
            <Typography variant="body1" paragraph>
              Name: {formData.firstName} {formData.lastName}
            </Typography>
            <Typography variant="body1" paragraph>
              Plan: {formData.planId} ({formData.billingInterval})
            </Typography>
            <Typography variant="body1" paragraph>
              Price: ${formData.planPrice}/{formData.billingInterval === 'yearly' ? 'year' : 'month'}
            </Typography>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom textAlign="center">
          Premium Signup
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ py: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : activeStep === steps.length - 1 ? (
                'Complete Signup'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <Button
            color="primary"
            onClick={() => navigate('/login')}
          >
            Sign in
          </Button>
        </Typography>
      </Box>
    </Container>
  );
}
